"use client";

import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useRouter } from "next/navigation";
import { DestinationSearch } from "../home/DestinationSearch";
import DateRangePicker from "../ui/date-range-picker";
import { PeopleCountInput } from "../home/PeopleCountInput";
import { Button } from "../ui/button";
import { WeatherCarousel } from "./weather/WeatherCarousel";
import { useWeatherQuery } from "@/hooks/useWeatherQuery";
import { type DateRange } from "react-day-picker";
import { format } from "date-fns";
import MobileSearchStepper from "./MobileSearchStepper";
import { useCities } from "@/lib/react-query/queries/references/use-cities";
import type { City } from "@/types/city";

// Define Zod schema for form validation
const searchSchema = z.object({
  destination: z.string().min(1, "مقصد را وارد کنید"),
  dateRange: z.object({
    from: z.date({ required_error: "تاریخ شروع را وارد کنید" }),
    to: z.date().optional(),
  }),
  peopleCount: z.number().min(1, "تعداد مسافر باید حداقل 1 نفر باشد"),
});

type SearchFormData = z.infer<typeof searchSchema>;

interface SearchBarProps {
  destination: string;
  checkIn: string;
  checkOut: string;
  peopleCount: number;
  city?: string;
}

export const SearchBar = React.memo(function SearchBar({
  destination,
  checkIn,
  checkOut,
  peopleCount,
  city,
}: SearchBarProps) {
  const router = useRouter();
  const { data: citiesResponse } = useCities();
  const cities = citiesResponse?.data || [];
  const cityRecord = React.useMemo(
    () => cities.find((c: City) => c.slug === destination),
    [cities, destination],
  );
  const weatherCity =
    cityRecord?.name || city || destination.split("--")[0] || "";
  const { data: weatherData } = useWeatherQuery(weatherCity, checkIn, checkOut);

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SearchFormData>({
    defaultValues: {
      destination,
      dateRange: {
        from: new Date(checkIn),
        to: checkOut ? new Date(checkOut) : undefined,
      },
      peopleCount,
    },
    resolver: zodResolver(searchSchema),
  });

  // Convert city slug to city--state format
  const convertSlugToCityState = (slug: string): string => {
    const cityData = cities.find((c: City) => c.slug === slug);
    if (cityData) {
      return cityData.slug;
    }
    // Fallback: if slug is already in city--state format, return as is
    // Otherwise, return the slug (shouldn't happen in normal flow)
    return slug.includes("--") ? slug : slug;
  };

  // State for managing autofocus chain
  const [dateRangeOpen, setDateRangeOpen] = React.useState(false);
  const [peopleCountOpen, setPeopleCountOpen] = React.useState(false);
  const peopleCountOpeningRef = React.useRef(false);

  const onSubmit = (data: SearchFormData) => {
    // Convert destination slug to city--state format
    const cityState = convertSlugToCityState(data.destination);

    // Build query params (without destination)
    const params = new URLSearchParams();
    params.set("checkIn", format(data.dateRange.from, "yyyy-MM-dd"));
    params.set("peopleCount", data.peopleCount.toString());

    if (data.dateRange.to) {
      params.set("checkOut", format(data.dateRange.to, "yyyy-MM-dd"));
    }

    // Navigate to new route with city--state in path and query params
    const newPath = `/search/city/${cityState}`;
    const queryString = params.toString();
    const newUrl = queryString ? `${newPath}?${queryString}` : newPath;
    router.push(newUrl);
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from) {
      setValue("dateRange.from", range.from);
    }
    if (range?.to) {
      setValue("dateRange.to", range.to);
    }
  };

  return (
    <div className="sticky top-0 md:top-[85px] z-20 bg-white border-b border-black-50 w-full">
      <div className="container-7xl px-0 md:px-5 xl:px-20 py-4 flex flex-col md:flex-row gap-4 items-center md:min-h-24">
        <MobileSearchStepper
          onSubmit={handleSubmit(onSubmit)}
          watch={watch}
          setValue={setValue as (name: string, value: any) => void}
          errors={{
            destination: errors.destination?.message,
            dateRange: errors.dateRange?.message,
            peopleCount: errors.peopleCount?.message,
          }}
        />
        <div className="hidden lg:flex flex-1 flex-col md:flex-row w-full">
          <DestinationSearch
            value={watch("destination")}
            onChange={(value) => setValue("destination", value)}
            error={errors.destination?.message}
            className="flex items-center pl-6 w-40"
            disabled={false}
            onSelectionComplete={() => {
              // When destination is selected, open date range picker
              setTimeout(() => setDateRangeOpen(true), 100);
            }}
          />
          <div className="w-px h-6 hidden md:block bg-gray-100 self-center"></div>
          <DateRangePicker
            dateRange={watch("dateRange")}
            onSelect={handleDateRangeChange}
            className="px-3"
            disabled={false}
            open={dateRangeOpen}
            onOpenChange={setDateRangeOpen}
            onSelectionComplete={() => {
              // When date range is confirmed, open people count input
              // Use a longer delay to ensure date picker is fully closed
              setTimeout(() => {
                peopleCountOpeningRef.current = true;
                setPeopleCountOpen(true);
                // Reset the flag after a short delay
                setTimeout(() => {
                  peopleCountOpeningRef.current = false;
                }, 200);
              }, 200);
            }}
          />
          <div className="w-px h-6 hidden md:block bg-gray-100 self-center"></div>
          <PeopleCountInput
            value={watch("peopleCount")}
            onChange={(value) => setValue("peopleCount", value)}
            error={errors.peopleCount?.message}
            className="flex items-center gap-1.5 cursor-pointer border-none mr-6"
            disabled={false}
            open={peopleCountOpen}
            onOpenChange={(open) => {
              // Prevent closing if we just programmatically opened it
              if (!open && peopleCountOpeningRef.current) {
                return;
              }
              setPeopleCountOpen(open);
            }}
          />
        </div>
        {weatherData?.days && weatherData?.days?.length > 0 && city && (
          <WeatherCarousel days={weatherData?.days} city={city} />
        )}
        <Button
          variant="default"
          className="hidden lg:flex min-w-[150px] h-[46px] rounded-[13px] text-sm font-normal bg-primary-500 hover:bg-primary-600 text-white border-0"
          onClick={handleSubmit(onSubmit)}
        >
          تغییر جستجو
        </Button>
      </div>
    </div>
  );
});
